// * Import required libraies
import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import axios from "axios";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";

// * Import Custom Component
// Layouts
import LayoutTopSideBottom from "../../../layouts/LayoutTopSideBottom/LayoutTopSideBottom";

// Common Kit
import WorkspaceGrid from "./WorkspaceGrid";
import WorkspaceTable from "./WorkspaceTable";
import Loader from "../../../components/Loader/Loader";
import ErrorHandler from "../../../components/ErrorHandler/ErrorHandler";
import SigviewTypography from "../../../components/Common/SigviewTypography";
import SigviewIconsButton from "../../../components/Common/SigviewIconsButton";

// * Import data/utils
import { wrapperOrgViewReq } from "../../../utils/chartObjectUtils";
import { initialWsFormScreenValue } from "../../../utils/analyzeUtils";

// Import config
import { config } from "../../../config/config";

// * Import Actions
import { updateStandaloneWsForm } from "../../../redux/actions";

// Import Api's
import { getAllWorkbooks } from "../../../services/api";

// Import Images
import activeGrid from "../../../../assets/images/activeGrid.svg";
import inActiveGrid from "../../../../assets/images/inActiveGrid.svg";
import activeTable from "../../../../assets/images/activeTable.svg";
import inActiveTable from "../../../../assets/images/inActiveTable.svg";
import SigviewSearchField from "../../../components/Common/SigviewSearchField";
import SigviewTooltip from "../../../components/Common/SigviewTooltip";

const initialData = {
  status: "loading",
  message: "",
  result: [],
};
const WorkspacePage = (props) => {
  const { dispatch: ReduxDispatcher = () => {}, user = {} } = props;
  const history = useHistory();
  const [view, setView] = useState("grid");
  const [workbooks, setWorkbooks] = useState(initialData);
  const [searchQuery, setSearchQuery] = useState("");
  const [reloadWorkbooks, setReloadWorkbooks] = useState(null);
  const handleViewChange = (newView) => {
    if (newView !== null) setView(newView);
  };
  const sidenavProps = { tabName: "allWorkbooks" };
  const handleCreateNewDialog = () => {
    history.push("/analyze/dashboard/create");
    const value = "create";
    handleWsFormChange("type", value);
    handleShowWorkbooksDrawer();
  };
  const handleShowWorkbooksDrawer = (value) => {
    const newScreen = { ...initialWsFormScreenValue, allWorkbooks: value };
    handleWsFormChange("screen", newScreen);
  };
  const handleWsFormChange = (key, value) => {
    const payload = { key, value };
    const action = updateStandaloneWsForm(payload);
    ReduxDispatcher(action);
  };
  // to call the default api here
  useEffect(() => {
    //Make fetch call using axios
    const source = axios.CancelToken.source();

    let runFlag = !user.screen.isDashboardLoading;
    if (runFlag) {
      const fetchProps = {
        payload: {
          orgViewReq: wrapperOrgViewReq(user),
          email: user.reqMetadata?.email,
        },
        cancelToken: source.token,
      };
      const getAllWorkbooksPromise = getAllWorkbooks(fetchProps);
      getAllWorkbooksPromise
        .then((responseData) => {
          const newData = {
            status: "success",
            message: "",
            result: { workbookList: responseData.result.data },
          };
          setWorkbooks(newData);
        })
        .catch((json) => {
          if (json.error !== config.hardCoded.queryCancelled) {
            console.groupCollapsed("UI ERROR");
            console.log("JSON -> ", json);
            console.log("ERROR -> ", json.error);
            console.groupEnd();

            const newData = {
              status: "error",
              message: "",
              result: json.error || config.messages.uiErrorMessage,
            };
            setWorkbooks(newData);
          }
        });
    }

    //Clean-up function to cancel all pending fetch calls
    return () => {
      //Cancel all previous fetch calls
      runFlag = false;
      if (source) source.cancel();
    };
  }, [user.screen.isDashboardLoading, reloadWorkbooks]);
  const handleReportSearchFieldChange = (event, value) => setSearchQuery(value);

  return (
    <>
      {workbooks.status === "loading" && (
        <LayoutTopSideBottom>
          <Loader />
        </LayoutTopSideBottom>
      )}
      {workbooks.status === "error" && (
        <LayoutTopSideBottom>
          <ErrorHandler
            message={workbooks.message}
            reloadFlag={true}
            onReload={() => {
              window.reload();
            }}
          />
        </LayoutTopSideBottom>
      )}
      {workbooks.status === "success" && (
        <LayoutTopSideBottom sidenavProps={sidenavProps}>
          <Box p={3}>
            {/* Header */}
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={3}
            >
              <Box display="flex" alignItems="center">
                <SigviewTypography
                  variant="heading"
                  style={{ fontSize: "20px" }}
                >
                  My Workspace
                </SigviewTypography>
                <Box
                  className="search-container"
                  style={{ minWidth: "250px", marginLeft: "16px" }}
                >
                  <SigviewSearchField
                    onChange={handleReportSearchFieldChange}
                    placeholder="Search"
                    customStyle={{
                      wrapperBgColor: "#f8f9fd",
                      wrapperBorderColor: "1px solid #fff",
                      wrapperBorderHover: "#fff",
                      wrapperBorderFocus: "#fff",
                    }}
                  />
                </Box>
              </Box>

              <Box display="flex" gap={2} alignItems="center">
                <Box display="flex" gap="6px">
                  {/* Grid View */}
                  <SigviewTooltip title="Grid View" placement="bottom">
                    <Box
                      onClick={() => handleViewChange("grid")}
                      sx={{
                        cursor: "pointer",
                        borderRadius: "5px",
                        boxShadow:
                          view === "grid"
                            ? "0px 4px 12px 0px rgba(84, 72, 206, 0.2)"
                            : "none",
                        p: "6px", // reduced padding
                        transition: "box-shadow 0.2s",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <img
                        src={view === "grid" ? activeGrid : inActiveGrid}
                        alt="Grid View"
                        style={{ width: 17, height: 17 }}
                      />
                    </Box>
                  </SigviewTooltip>

                  {/* Table View */}
                  <SigviewTooltip title="Table View" placement="bottom">
                    <Box
                      onClick={() => handleViewChange("table")}
                      sx={{
                        cursor: "pointer",
                        borderRadius: "5px",
                        boxShadow:
                          view === "table"
                            ? "0px 4px 12px 0px rgba(84, 72, 206, 0.2)"
                            : "none",
                        p: "6px",
                        transition: "box-shadow 0.2s",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <img
                        src={view === "table" ? activeTable : inActiveTable}
                        alt="Table View"
                        style={{ width: 17, height: 17 }}
                      />
                    </Box>
                  </SigviewTooltip>
                </Box>

                <SigviewIconsButton
                  customClassName="createNewWorkspace-GA"
                  onClick={handleCreateNewDialog}
                  title="Create New Workspace"
                  iconName="add"
                  iconPosition="right"
                  outerStyle={{
                    minWidth: "160px",
                    borderRadius: "999em",
                    height: "26px",
                    padding: "4px 12px",
                  }}
                  textStyle={{
                    lineHeight: 2,
                    margin: "0px 6px 0px 0px !important"
                  }}
                />
              </Box>
            </Box>

            {/* View Switch */}
            {view === "grid" ? (
              <WorkspaceGrid
                workbooks={workbooks}
                searchQuery={searchQuery}
                setReloadWorkbooks={setReloadWorkbooks}
              />
            ) : (
              <WorkspaceTable
                workbooks={workbooks}
                searchQuery={searchQuery}
                setReloadWorkbooks={setReloadWorkbooks}
              />
            )}
          </Box>
        </LayoutTopSideBottom>
      )}
    </>
  );
};
const mapStateToProps = (state) => ({
  user: state.user,
  allData: state.data,
  globalFilters: state.globalFilters,
});

export default connect(mapStateToProps)(WorkspacePage);
